import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ComponentCanDeactivate } from 'src/app/services/pending-changes.guard';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Actions,
  EmailFooterMapping,
  Section,
  TargetJurisdictionResources,
  WorkflowConfigurationSaveAction,
  WorkflowCopyConfiguration,
  WorkflowCopyOptions
} from 'src/app/models';
import { CustomList, CustomListItem } from 'src/app/models/custom-list';
import {
  SecurityService,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';
import { CKEditorComponent } from '@ckeditor/ckeditor5-angular';
import { EmailFooter } from 'src/app/models/email-footer';
import { FeeRecipient } from 'src/app/models/fee-recipient';
import { EmailActivityEditorComponent } from '../../activities/email-activity/email-activity-editor/email-activity-editor.component';
import { ToastrService } from 'ngx-toastr';
import { ModalConfirmComponent } from 'src/app/components/system/modal-confirm/modal-confirm.component';
import { Activity, ActivityModel } from 'src/app/models/activities';

@Component({
  selector: 'wm-map-workflow-settings',
  templateUrl: './map-workflow-settings.component.html',
  styleUrl: './map-workflow-settings.component.css',
  standalone: false
})
export class MapWorkflowSettingsComponent
  implements OnInit, ComponentCanDeactivate {
  @ViewChild('htmlContentEditor', { static: false })
  htmlContentEditor: CKEditorComponent;
  @ViewChild('finishModal', { static: false })
  finishModal: ModalConfirmComponent;
  @ViewChildren('emailActivityEditors') emailActivityEditors: QueryList<
    EmailActivityEditorComponent
  >;

  @Input() sourceWorkflowId: string;
  @Input() targetJurisdictionId: string;
  @Input() useTargetJurisdictionInput = false;

  loading = true;
  isSaving = false;
  isProcessing = false;
  isGlobalAdmin = false;
  WorkflowConfigurationSaveAction = WorkflowConfigurationSaveAction;
  config: WorkflowCopyConfiguration;
  targetResources: TargetJurisdictionResources;
  sourceWorkflowName: string;
  targetCustomLists: CustomList[] = [];
  targetRoleMemberCustomLists: CustomList[] = [];

  // blockers
  mustCreateContractorType: boolean = false;
  blockers: string[] = [];

  sections: Section[] = [
    {
      key: 'workflow-settings',
      label: 'Review & Edit Workflow Settings',
      enabled: true,
      completed: false
    },
    {
      key: 'roles',
      label: 'Configure Workflow Roles',
      enabled: false,
      completed: false
    },
    {
      key: 'custom-lists',
      label: 'Configure Workflow Custom Lists & Items',
      enabled: false,
      completed: false
    },
    {
      key: 'fee-recipients',
      label: 'Configure Fee Recipients',
      enabled: false,
      completed: false
    },
    {
      key: 'fees',
      label: 'Review & Edit Workflow Fees',
      enabled: false,
      completed: false
    },
    {
      key: 'email-footers',
      label: 'Configure Workflow Email Footers',
      enabled: false,
      completed: false
    },
    {
      key: 'emails',
      label: 'Review & Edit Workflow Emails',
      enabled: false,
      completed: false
    },
    {
      key: 'print-templates',
      label: 'Review & Edit Workflow Print Templates',
      enabled: false,
      completed: false
    },
    {
      key: 'contractors',
      label: 'Configure Contractor Types',
      enabled: false,
      completed: false
    },
    {
      key: 'mapService',
      label: 'Select Map Service',
      enabled: false,
      completed: false
    }
  ];
  expandedSectionIndex = 0;
  workflowSaveId: string;
  finishText: string;

  // email accordion
  emailSections: Section[] = [];
  expandedEmailSectionIndex = 0;

  // form groups
  form: UntypedFormGroup;
  workflowSettingsForm: UntypedFormGroup;
  roleMappingsForm: UntypedFormGroup;
  customListMappingsForm: UntypedFormGroup;
  feeRecipientMappingsForm: UntypedFormGroup;
  feeUpdatesForm: UntypedFormGroup;
  emailFooterMappingsForm: UntypedFormGroup;
  emailUpdatesForm: UntypedFormGroup;
  printTemplateUpdatesForm: UntypedFormGroup;
  contractorUpdatesForm: UntypedFormGroup;
  mapServiceForm: UntypedFormGroup;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _context: WorkflowContextService,
    private _workflowSvc: WorkflowService,
    private _securitySvc: SecurityService,
    private _fb: UntypedFormBuilder,
    private _toastrSvc: ToastrService,
    private _ref: ChangeDetectorRef
  ) {}

  get allSectionsCompleted(): boolean {
    for (let i = 0; i < this.sections.length; i++) {
      if (this.sections[i].enabled && !this.sections[i].completed) {
        return false;
      }
    }

    return true;
  }

  get allEmailSectionsCompleted(): boolean {
    for (let i = 0; i < this.emailSections.length; i++) {
      if (this.emailSections[i].enabled && !this.emailSections[i].completed) {
        return false;
      }
    }

    return true;
  }

  @HostListener('window:beforeunload', ['$event'])
  canDeactivate(): boolean {
    const result = !(
      this.workflowSettingsForm?.dirty ||
      this.roleMappingsForm?.dirty ||
      this.customListMappingsForm?.dirty ||
      this.feeRecipientMappingsForm?.dirty ||
      this.feeUpdatesForm?.dirty ||
      this.emailFooterMappingsForm?.dirty ||
      this.emailUpdatesForm?.dirty ||
      this.printTemplateUpdatesForm?.dirty ||
      this.contractorUpdatesForm?.dirty ||
      this.mapServiceForm?.dirty
    );
    return result;
  }

  ngOnInit() {
    this.workflowSettingsForm = this._fb.group({
      workflowName: this._fb.control('', [Validators.required]),
      description: this._fb.control('', [Validators.nullValidator])
    });
    this.roleMappingsForm = new UntypedFormGroup({});
    this.customListMappingsForm = new UntypedFormGroup({});
    this.feeRecipientMappingsForm = new UntypedFormGroup({});
    this.feeUpdatesForm = new UntypedFormGroup({});
    this.emailFooterMappingsForm = new UntypedFormGroup({});
    this.emailUpdatesForm = new UntypedFormGroup({});
    this.printTemplateUpdatesForm = new UntypedFormGroup({});
    this.contractorUpdatesForm = new UntypedFormGroup({});
    this.mapServiceForm = new UntypedFormGroup({});

    const init = () => {
      if (this.workflowSaveId) {
        this._workflowSvc
          .loadSavedWorkflowConfigurationOptions(this.workflowSaveId)
          .subscribe(options => {
            this.setConfigurationOptions(options);

            this.setExpandedSectionIndex(this.expandedSectionIndex);

            if (this.config.emailUpdates?.emailSections) {
              this.setExpandedEmailSectionIndex(this.expandedEmailSectionIndex);
            }
          });
      } else {
        this._workflowSvc
          .getCopyWorkflowConfigurationOptions(
            this.sourceWorkflowId,
            this.targetJurisdictionId
          )
          .subscribe(options => {
            this.setConfigurationOptions(options);
          });
      }
    };

    this._securitySvc
      .isLoginEntitled(Actions.DO_ANYTHING)
      .subscribe(isEntitled => {
        this.isGlobalAdmin = isEntitled;

        this._route.params.subscribe(params => {
          if (params['workflowId']) {
            this.sourceWorkflowId = params['workflowId'];
          }

          if (params['workflowSaveId']) {
            this.workflowSaveId = params['workflowSaveId'];
          }

          if (this.workflowSaveId) {
            init();
          } else {
            if (this.useTargetJurisdictionInput) {
              init();
            } else if (
              this._route.parent &&
              this._route.parent.parent &&
              this._route.parent.parent.params
            ) {
              this._route.parent.parent.params.subscribe(clientViewParams => {
                if (clientViewParams['clientId']) {
                  this.targetJurisdictionId = clientViewParams['clientId'];
                }

                init();
              });
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.workflowSettingsForm.removeControl('workflowName');
    this.workflowSettingsForm.removeControl('description');

    if (this.config?.feeRecipientMappings?.mappings) {
      for (let mapping of this.config.feeRecipientMappings.mappings) {
        this.feeRecipientMappingsForm.removeControl(mapping.sourceId);
        this.feeRecipientMappingsForm.removeControl(
          mapping.sourceId + '_ignored'
        );
      }
    }

    if (this.config?.customListMappings?.mappings) {
      const sourceListIds = this.config.customListMappings.mappings.map(
        rm => rm.sourceListId
      );

      if (sourceListIds && sourceListIds.length > 0) {
        for (let listId of sourceListIds) {
          if (this.customListMappingsForm.contains(listId)) {
            this.customListMappingsForm.removeControl(listId);
          }

          const sourceListItemIds = this.config.customListMappings.mappings
            .filter(clm => clm.sourceListId == listId)
            .map(rm => rm.sourceListId);

          for (let itemId of sourceListItemIds) {
            if (this.customListMappingsForm.contains(itemId)) {
              this.customListMappingsForm.removeControl(itemId);
            }
          }
        }
      }
    }

    if (this.config?.feeUpdates?.feeDataEntities) {
      const feeTemplateCodes = this.config.feeUpdates.feeDataEntities.map(
        fde => fde.templateCode
      );

      if (feeTemplateCodes && feeTemplateCodes.length > 0) {
        for (let templateCode of feeTemplateCodes) {
          this.feeUpdatesForm.removeControl(templateCode);
        }
      }
    }
  }

  defaultTargetListItems(sourceListId: string, targetListId: string) {
    if (sourceListId) {
      const sourceList = this.config.customListMappings.mappings.find(
        clm => clm.sourceListId === sourceListId
      );

      if (targetListId) {
        const targetList = this.targetResources.customLists.find(
          cl => cl.id === targetListId
        );

        if (targetList) {
          // map source list items to target list items by value
          for (let itemMapping of sourceList.itemMappings) {
            const targetItem = targetList.items.find(
              tli => tli.value === itemMapping.sourceItem.value
            );

            if (targetItem) {
              itemMapping.targetItemId = targetItem.id;
            } else {
              itemMapping.targetItemId = null;
            }
          }
        }
      }
    }
  }

  setConfigurationOptions(options: WorkflowCopyOptions) {
    // set config object
    this.config = options.sourceWorkflowElements;

    // set target resources
    this.targetJurisdictionId = options.targetJurisdictionResources.targetJurisdictionId;
    this.targetResources = options.targetJurisdictionResources;

    this.targetCustomLists = this.targetResources.customLists?.filter(cl => !cl.roleId) || [];
    this.targetRoleMemberCustomLists = this.targetResources.customLists?.filter(cl => cl.roleId) || [];

    // set original source workflow name
    this.sourceWorkflowName = options.originalSourceWorkflowName;

    this.finishText = this.config.action == WorkflowConfigurationSaveAction.Import
      ? 'Finish Import Workflow'
      : 'Finish Copy Workflow'

    // set blockers
    if (options.mustCreateContractorType) {
      this.mustCreateContractorType = options.mustCreateContractorType;

      this.blockers.push('At least one Contractor Type must be created.');
    }

    if (options.mustConfigureParcelDataSource) {
      this.blockers.push(
        'Must have a Parcel Data connection configured in Data Connections.'
      );
    }

    if (options.mustConfigureMapService) {
      this.blockers.push('Must have at least one Map Service configured.');
    }

    // enable relevant sections and initialize their completed status
    this.sections[0].completed = this.config.workflowUpdates.completed;

    if (
      this.config.roleMappings?.mappings &&
      this.config.roleMappings.mappings.length > 0
    ) {
      for (let roleMapping of this.config.roleMappings.mappings) {
        if (!roleMapping.targetRoleId) {
          roleMapping.targetRoleId = null;
        }
        // Add form controls for each role mapping
        this.roleMappingsForm.addControl(
          roleMapping.sourceRoleId,
          new UntypedFormControl(roleMapping.targetRoleId)
        );
      }

      this.sections[1].enabled = true;

      this.sections[1].completed = this.config.roleMappings.completed;
    }

    if (
      this.config.customListMappings?.mappings &&
      this.config.customListMappings.mappings.length > 0
    ) {
      for (let customListMapping of this.config.customListMappings.mappings) {
        if (!customListMapping.targetListId) {
          customListMapping.targetListId = null;
        }

        if (!customListMapping.targetRoleId) {
          customListMapping.targetRoleId = null;
        }

        // Add form control for the custom list mapping
        this.customListMappingsForm.addControl(
          customListMapping.sourceListId,
          new UntypedFormControl(
            customListMapping.targetListId,
            this.targetJurisdictionId && customListMapping.sourceRoleName
              ? Validators.required
              : Validators.nullValidator
          )
        );

        for (let listItemMapping of customListMapping.itemMappings) {
          this.customListMappingsForm.addControl(
            listItemMapping.sourceItem.id,
            new UntypedFormControl(
              listItemMapping.targetItemId,
              Validators.nullValidator
            )
          );

          if (!listItemMapping.targetItemId) {
            listItemMapping.targetItemId = null;
          }
        }
      }

      this.sections[2].enabled = true;

      this.sections[2].completed = this.config.customListMappings.completed;
    }

    if (
      this.config.feeRecipientMappings?.mappings &&
      this.config.feeRecipientMappings.mappings.length > 0
    ) {
      for (let mapping of this.config.feeRecipientMappings.mappings) {
        if (!mapping.targetId) {
          mapping.targetId = null;
        }
        // Add form control for each mapping
        this.feeRecipientMappingsForm.addControl(
          mapping.sourceId,
          new UntypedFormControl(mapping.targetId)
        );
        this.feeRecipientMappingsForm.addControl(
          mapping.sourceId + '_ignored',
          new UntypedFormControl(mapping.isIgnored)
        );
      }

      this.sections[3].enabled = true;

      this.sections[3].completed = this.config.feeRecipientMappings.completed;
    }

    if (
      this.config.feeUpdates?.feeDataEntities &&
      this.config.feeUpdates.feeDataEntities.length > 0
    ) {
      let fdeActivities = [];

      // get distinct list of activities
      for (let fde of this.config.feeUpdates.feeDataEntities) {
        if (
          !fdeActivities.some(
            fdea => fdea.id === fde.parentActivitySurrogate.id
          )
        ) {
          fdeActivities.push(fde.parentActivitySurrogate);
        }
      }

      for (let activity of fdeActivities) {
        this.populateDataEntitiesBeforeMeCache(activity);
      }

      const templateCodes = this.config.feeUpdates.feeDataEntities.map(
        fde => fde.templateCode
      );

      for (let tc of templateCodes) {
        this.feeUpdatesForm.addControl(tc, new UntypedFormGroup({}));
      }

      this.sections[4].enabled = true;

      this.sections[4].completed = this.config.feeUpdates.completed;
    }

    if (
      this.config.emailFooterMappings?.mappings &&
      this.config.emailFooterMappings.mappings.length > 0
    ) {
      for (let footerMapping of this.config.emailFooterMappings.mappings) {
        if (!footerMapping.targetId) {
          footerMapping.targetId = null;
        }

        this.resetTargetEmailFooterContent(footerMapping);

        const footerGroup = new UntypedFormGroup({
          targetId: new UntypedFormControl(footerMapping.targetId),
          isIgnored: new UntypedFormControl(footerMapping.isIgnored)
        });
        this.emailFooterMappingsForm.addControl(
          footerMapping.sourceEmailFooter.id,
          footerGroup
        );
      }

      this.sections[5].enabled = true;

      this.sections[5].completed = this.config.emailFooterMappings.completed;
    }

    if (
      this.config.emailUpdates?.emailActivities &&
      this.config.emailUpdates.emailActivities.length > 0
    ) {
      for (let activity of this.config.emailUpdates.emailActivities) {
        this.emailSections = this.config.emailUpdates.emailSections;

        this.populateDataEntitiesBeforeMeCache(activity);

        this.emailUpdatesForm.addControl(activity.id, new UntypedFormGroup({}));
      }

      this.sections[6].enabled = true;

      this.sections[6].completed = this.config.emailUpdates.completed;
    }

    if (
      this.config.printTemplateUpdates?.printTemplateActivities &&
      this.config.printTemplateUpdates.printTemplateActivities.length > 0
    ) {
      for (let activity of this.config.printTemplateUpdates
        .printTemplateActivities) {
        this.populateDataEntitiesBeforeMeCache(activity);

        this.printTemplateUpdatesForm.addControl(
          activity.id,
          new UntypedFormGroup({})
        );
      }

      this.sections[7].enabled = true;

      this.sections[7].completed = this.config.printTemplateUpdates.completed;
    }

    if (
      (this.config.selectContractorUpdates?.contractorSelectionActivities &&
        this.config.selectContractorUpdates.contractorSelectionActivities
          .length > 0) ||
      (this.config.selectContractorUpdates?.selectContractorDataEntities &&
        this.config.selectContractorUpdates.selectContractorDataEntities
          .length > 0)
    ) {
      // don't activate this section if Template Library is the target
      if (this.targetJurisdictionId) {
        if (
          this.config.selectContractorUpdates?.contractorSelectionActivities &&
          this.config.selectContractorUpdates.contractorSelectionActivities
            .length > 0
        ) {
          const selectContractorActivityIds = this.config.selectContractorUpdates.contractorSelectionActivities.map(
            csa => csa.id
          );

          for (let id of selectContractorActivityIds) {
            this.contractorUpdatesForm.addControl(id, new UntypedFormGroup({}));
          }
        }

        if (
          this.config.selectContractorUpdates?.selectContractorDataEntities &&
          this.config.selectContractorUpdates.selectContractorDataEntities
            .length > 0
        ) {
          const templateCodes = this.config.selectContractorUpdates.selectContractorDataEntities.map(
            scde => scde.templateCode
          );

          for (let tc of templateCodes) {
            this.contractorUpdatesForm.addControl(tc, new UntypedFormGroup({}));
          }
        }

        this.sections[8].enabled = true;

        this.sections[8].completed = this.config.selectContractorUpdates.completed;
      }
    }

    if (
      this.targetResources.mapServices &&
      this.targetResources.mapServices.length > 0
    ) {
      this.mapServiceForm.addControl(
        'mapServiceTargetId',
        this._fb.control(this.config.mapServiceConfiguration.targetId, [
          Validators.required
        ])
      );

      this.sections[9].enabled = true;

      this.sections[9].completed = this.config.mapServiceConfiguration.completed;
    }

    this.loading = false;

    this._ref.detectChanges();
  }

  populateDataEntitiesBeforeMeCache(activity: Activity<ActivityModel>) {
    this._workflowSvc.clearDataEntityBeforeMeCache(activity.id);

    this._workflowSvc
      .getDataEntitiesBeforeMe(
        null,
        activity,
        null,
        [WorkflowService.DATA_ENTITIES.RichText.code],
        true,
        this.config.sourceWorkflowVersionId
      )
      .subscribe();
  }

  getTargetCustomListItems(id: string): CustomListItem[] {
    return this.targetResources.customLists.find(tjcl => tjcl.id === id)?.items;
  }

  getTargetCustomListById(id: string): CustomList {
    return this.targetResources.customLists.find(tjcl => tjcl.id === id);
  }

  updateFeeRecipientUsage() {
    // If the feeUpdates were already completed
    // prior to Fee Recipient Mappings being completed, then feeUpdates will need reviewed and completed again.
    if (this.config.feeUpdates.completed) {
      this.sections[4].completed = false;

      this.config.feeUpdates.completed = false;
    }

    for (let recipientMapping of this.config.feeRecipientMappings.mappings) {
      const removeSourceRecipientFromAvailable = (
        availableFeeRecipients: FeeRecipient[]
      ) => {
        const indexToRemove = availableFeeRecipients.findIndex(
          afr => afr.id === recipientMapping.sourceId
        );

        if (indexToRemove !== -1) {
          availableFeeRecipients.splice(indexToRemove, 1);
        }
      };

      let feeDEsReferencingThisSourceFeeRecipient = this.config.feeUpdates.feeDataEntities.filter(
        feeDE =>
          feeDE.clientFeeRecipient &&
          feeDE.clientFeeRecipient.id === recipientMapping.sourceId
      );

      if (recipientMapping.isIgnored) {
        // for any DE that has this source ClientFeeRecipient configured:
        //      -clear the ClientFeeRecipient
        //      -remove this source Recipient from AvailableFeeRecipients
        // this was not an available option on any other Fee DE that did not originate with this being selected as its ClientFeeRecipient, so
        // this means we only need to do this for Fee DEs that are currently referencing it.
        for (let feeDataEntity of feeDEsReferencingThisSourceFeeRecipient) {
          feeDataEntity.clientFeeRecipient = null;

          removeSourceRecipientFromAvailable(
            feeDataEntity.availableFeeRecipients
          );
        }
      } else {
        if (!recipientMapping.targetId) {
          // no changes needed for FeeDEs, if they reference this source FeeRecipient, they can stay that way.
          // The server is responsible for giving the source Fee Recipient a new identity in the destination,
          // and swapping in that new reference on all Fee DEs that use this source Recipient
        } else {
          // for any DE that has this source ClientFeeRecipient configured:
          //    -set ClientFeeRecipient to the selected target recipient
          //    -remove this source Recipient from AvailableFeeRecipients
          for (let feeDataEntity of feeDEsReferencingThisSourceFeeRecipient) {
            const targetFeeRecipient = this.targetResources.feeRecipients.find(
              fr => fr.id == recipientMapping.targetId
            );

            if (targetFeeRecipient) {
              feeDataEntity.clientFeeRecipient = { ...targetFeeRecipient };
            }

            removeSourceRecipientFromAvailable(
              feeDataEntity.availableFeeRecipients
            );
          }
        }
      }
    }
  }

  resetTargetEmailFooterContent(emailFooterMapping: EmailFooterMapping) {
    // find the mapping and set its targetEmailFooterContent to the sourceEmailFooter content
    let sourceEmailFooterMapping = this.config.emailFooterMappings.mappings.find(
      efm =>
        efm.sourceEmailFooter.id === emailFooterMapping.sourceEmailFooter.id
    );

    if (
      !emailFooterMapping.targetId &&
      !sourceEmailFooterMapping.targetEmailFooterContent
    ) {
      sourceEmailFooterMapping.targetEmailFooterContent =
        sourceEmailFooterMapping.sourceEmailFooter.htmlContent;
    }
  }

  updateTargetEmailFooterContent(content: string, sourceEmailFooterId: string) {
    let sourceEmailFooterMapping = this.config.emailFooterMappings.mappings.find(
      efm => efm.sourceEmailFooter.id === sourceEmailFooterId
    );

    sourceEmailFooterMapping.targetEmailFooterContent = content;
  }

  updateEmailFooterUsage() {
    // If the emailUpdates were already completed
    // prior to emailFooters being completed, then emailUpdates will need reviewed and completed again.
    if (this.config.emailUpdates.completed) {
      this.sections[6].completed = false;

      this.config.emailUpdates.completed = false;
    }

    for (let footerMapping of this.config.emailFooterMappings.mappings) {
      const removeSourceFooterFromAvailable = (
        availableEmailFooters: EmailFooter[]
      ) => {
        const indexToRemove = availableEmailFooters.findIndex(
          aef => aef.id === footerMapping.sourceEmailFooter.id
        );

        if (indexToRemove !== -1) {
          availableEmailFooters.splice(indexToRemove, 1);
        }
      };

      let emailActivitiesReferencingThisSourceEmailFooter = this.config.emailUpdates.emailActivities.filter(
        ea =>
          ea.model.emailFooterId &&
          ea.model.emailFooterId === footerMapping.sourceEmailFooter.id
      );

      if (footerMapping.isIgnored) {
        // for any DE that has this source EmailFooter configured:
        //      -clear the EmailFooterId on the model
        //      -remove this source EmailFooter from AvailableFooters
        // this was not an available option on any other Email Activity that did not originate with this being selected as its EmailFooterId, so
        // this means we only need to do this for Email Activities that are currently referencing it.
        for (let emailActivity of emailActivitiesReferencingThisSourceEmailFooter) {
          emailActivity.model.emailFooterId = null;

          removeSourceFooterFromAvailable(emailActivity.model.availableFooters);
        }
      } else {
        if (!footerMapping.targetId) {
          // If an EmailUpdates activity uses this source EmailFooterId, it can stay that way.
          // The server is responsible for giving the source EmailFooter a new identity in the destination,
          // and swapping in that new reference on all EmailUpdate activities that use this source EmailFooter.
          // What we must do at this point is update the html content of this footer anywhere it is an availableFooter,
          // because the user had the option of modifying it.
          for (let emailActivity of this.config.emailUpdates.emailActivities) {
            let availableFooterFromSource = emailActivity.model.availableFooters.find(
              af => af.id === footerMapping.sourceEmailFooter.id
            );

            if (availableFooterFromSource) {
              availableFooterFromSource.htmlContent =
                footerMapping.targetEmailFooterContent;
            }
          }
        } else {
          // for any Email Activity that has this source EmailFooterId configured:
          //    -set EmailFooterId to the selected target email footer id
          //    -remove this source EmailFooter from AvailableFooters
          for (let emailActivity of emailActivitiesReferencingThisSourceEmailFooter) {
            const targetEmailFooter = this.targetResources.emailFooters.find(
              ef => ef.id == footerMapping.targetId
            );

            if (targetEmailFooter) {
              emailActivity.model.emailFooterId = targetEmailFooter.id;
            }

            removeSourceFooterFromAvailable(
              emailActivity.model.availableFooters
            );
          }
        }
      }

      for (let emailActivityEditor of this.emailActivityEditors) {
        if (
          emailActivitiesReferencingThisSourceEmailFooter.some(
            eartsef => eartsef.id === emailActivityEditor.activity.id
          )
        ) {
          emailActivityEditor.init();
        }
      }
    }
  }

  saveForLater() {
    if (this.config.feeUpdates?.feeDataEntities?.length > 0) {
      for (let feeDE of this.config.feeUpdates.feeDataEntities) {
        feeDE.parentActivitySurrogate = null;
      }
    }

    this._workflowSvc
      .saveWorkflowCopyConfiguration(
        this.config,
        this.targetJurisdictionId,
        this.workflowSaveId
      )
      .subscribe(result => {
        if (result) {
          this.resetForms();
          this._router.navigate([
            '/admin/jurisdiction',
            this._context.client.id,
            'workflows',
            'list'
          ]);
        } else {
          this._toastrSvc.error(
            'Failed to save settings.  Please contact support if this issue persists.'
          );
        }
      });
  }

  resetForms() {
    this.workflowSettingsForm.reset();
    this.roleMappingsForm.reset();
    this.customListMappingsForm.reset();
    this.feeRecipientMappingsForm.reset();
    this.feeUpdatesForm.reset();
    this.emailFooterMappingsForm.reset();
    this.emailUpdatesForm.reset();
    this.printTemplateUpdatesForm.reset();
    this.contractorUpdatesForm.reset();
    this.mapServiceForm.reset();
  }

  nextEmailSection() {
    this.emailSections[this.expandedEmailSectionIndex].completed = true;

    let nextIdx = this.expandedEmailSectionIndex + 1;

    this.setExpandedEmailSectionIndex(nextIdx);
  }

  setExpandedEmailSectionIndex(index: number) {
    while (
      index < this.emailSections.length &&
      (!this.emailSections[index].enabled || this.emailSections[index].completed)
    ) {
      index++;
    }

    this.expandedEmailSectionIndex = index;

    if (this.allEmailSectionsCompleted) {
      this.expandedEmailSectionIndex = -1;
    }
  }

  nextSection() {
    this.isSaving = true;

    this.sections[this.expandedSectionIndex].completed = true;

    let nextIdx = this.expandedSectionIndex + 1;

    this.setExpandedSectionIndex(nextIdx);

    this.isSaving = false;
  }

  setExpandedSectionIndex(index: number) {
    while (
      index < this.sections.length &&
      (!this.sections[index].enabled || this.sections[index].completed)
    ) {
      index++;
    }

    this.expandedSectionIndex = index;

    if (this.allSectionsCompleted) {
      this.expandedSectionIndex = -1;
    }
  }

  finish() {
    this.isSaving = true;

    if (this.config.feeUpdates?.feeDataEntities?.length > 0) {
      for (let feeDE of this.config.feeUpdates.feeDataEntities) {
        feeDE.parentActivitySurrogate = null;
      }
    }

    this._workflowSvc
      .copyWorkflow({
        workflowId: this.sourceWorkflowId,
        targetClientId: this.targetJurisdictionId,
        configuration: this.config,
        workflowSaveId: this.workflowSaveId
      })
      .subscribe(
        newWorkflow => {
          if (newWorkflow) {
            this.resetForms();

            this._router.navigate([
              '/admin/jurisdiction',
              this._context.client.id,
              'workflows',
              'list'
            ]);
          } else {
            this._toastrSvc.error(
              'Failed to copy/import workflow.  Please contact support if this issue persists.'
            );
          }

          this.isSaving = false;
          this.isProcessing = false;

          this.finishModal.cancel();
        },
        e => {
          this._toastrSvc.error(
            'Failed to copy/import workflow.  Please contact support if this issue persists.'
          );
          this.isSaving = false;
          this.isProcessing = false;
          this.finishModal.cancel();
        }
      );
  }
}
